import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrderDetailDto } from '@crm/shared'
import { addOrderPositions, createOrderPosition } from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'
import { formatMoney } from '../orderFormat'
import { AddFromKpList } from './AddFromKpList'
import {
  EMPTY_DRAFT,
  NewOrderPositionForm,
  draftToDto,
  type NewPositionDraft,
} from './NewOrderPositionForm'

function apiMessage(e: unknown): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
    ?.error?.message
  return msg ?? 'Не удалось добавить позицию в заказ'
}

type Tab = 'kp' | 'new'

/** Ошибка заполнения формы — показываем её текст как есть (не ответ сервера). */
class FormError extends Error {}

/**
 * «Добавить позиции» в оформленный заказ (29.09.2026,
 * docs/features/order-editing.md). Две вкладки: позиции этой заявки из КП (с
 * ценой КП) и совсем новая позиция вручную. Добавленное идёт в выбранный счёт
 * (по умолчанию — последний действующий) или в заказ без счёта — тогда его
 * выгружают кнопкой «Выгрузить ещё счёт».
 */
export function AddOrderPositionsModal({
  order,
  onClose,
}: {
  order: OrderDetailDto
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('kp')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [draft, setDraft] = useState<NewPositionDraft>(EMPTY_DRAFT)
  const [error, setError] = useState<string | null>(null)

  // В отменённый и оплаченный счёт позиции не добавляются.
  const invoices = useMemo(
    () => order.invoices.filter((i) => i.status !== 'CANCELLED' && i.status !== 'PAID'),
    [order.invoices],
  )
  const [invoiceId, setInvoiceId] = useState<string>(invoices.at(-1)?.id ?? '')

  const done = (detail: OrderDetailDto, message: string) => {
    qc.setQueryData(['orders', order.id], detail)
    void qc.invalidateQueries({ queryKey: ['orders'], exact: false, refetchType: 'none' })
    void qc.invalidateQueries({ queryKey: ['request-items-by-request'] })
    void qc.invalidateQueries({ queryKey: ['requests'] })
    toast.success(message)
    onClose()
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (tab === 'kp') {
        return {
          detail: await addOrderPositions(order.id, {
            requestItemIds: [...selected],
            invoiceId: invoiceId || null,
          }),
          message: selected.size === 1 ? 'Позиция добавлена в заказ' : 'Позиции добавлены в заказ',
        }
      }
      const parsed = draftToDto(draft)
      if ('error' in parsed) throw new FormError(parsed.error)
      return {
        detail: await createOrderPosition(order.id, {
          ...parsed.dto,
          invoiceId: invoiceId || null,
        }),
        message: 'Новая позиция добавлена в заказ',
      }
    },
    onSuccess: ({ detail, message }) => done(detail, message),
    onError: (e) => setError(e instanceof FormError ? e.message : apiMessage(e)),
  })

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const canSubmit = tab === 'kp' ? selected.size > 0 : draft.partNumber.trim().length > 0
  const tabCls = (t: Tab) =>
    `rounded-lg px-3 py-1.5 text-sm ${
      tab === t ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Добавить позиции в заказ</h3>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          <button className={tabCls('kp')} onClick={() => setTab('kp')}>
            Из КП заявки
          </button>
          <button className={tabCls('new')} onClick={() => setTab('new')}>
            Новая позиция
          </button>
        </div>

        {tab === 'kp' ? (
          <AddFromKpList orderId={order.id} selected={selected} onToggle={toggle} />
        ) : (
          <NewOrderPositionForm draft={draft} onChange={setDraft} />
        )}

        <label className="mt-4 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Куда добавить</span>
          <select
            className="w-full rounded border border-gray-300 px-2 py-1.5"
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
          >
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                В {inv.invoiceNumber ? `счёт № ${inv.invoiceNumber}` : 'счёт без номера УНФ'} (
                {inv.items.length} поз., {formatMoney(inv.totalSum)})
              </option>
            ))}
            <option value="">Без счёта — выгружу отдельным счётом</option>
          </select>
        </label>
        <p className="mt-1 text-xs text-gray-500">
          После добавления скачайте шаблон счёта заново и обновите счёт в УНФ.
        </p>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Отмена
          </button>
          <button
            className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            onClick={() => {
              setError(null)
              mutation.mutate()
            }}
            disabled={!canSubmit || mutation.isPending}
          >
            {mutation.isPending
              ? 'Добавляю…'
              : tab === 'kp' && selected.size > 1
                ? `Добавить (${selected.size})`
                : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  )
}
